/**
 * Race Strategy Hook
 * 
 * Custom React hook that manages all race strategy state and calculations.
 * 
 * Following SOLID principles:
 * - Single Responsibility: Manages race strategy state and calculations
 * - Open/Closed: Extensible through dependency injection
 * - Dependency Inversion: Depends on abstractions (interfaces)
 * - Interface Segregation: Clean interfaces for different concerns
 * 
 * This hook orchestrates:
 * - User input state management
 * - Strategy calculations
 * - Data service integration
 * - Garage61 integration
 * - Fuel save analysis
 * 
 * Updated: 2025-01-15 15:35 - Fixed OAuth authentication flow
 */

import { useMemo, useState, useCallback } from "react";
import { 
  Strategy, 
  RaceEvent, 
  DriverData, 
  PitStopDetails,
  // Track,
  // CarClass,
  // IRacingCredentials,
  // IRacingTrack,
  // IRacingCarClass
  // Garage61Session
} from "../types/strategy";
import { StrategyCalculatorFactory, IStrategyCalculator } from "../utils/strategyCalculator";
// import { FuelSaveCalculator } from "../utils/strategyCalculator";
import { DataService } from "../utils/dataService";
// import { IRacingServiceFactory, IIRacingService } from "../utils/iracingService";
// import { Garage61ServiceFactory, IGarage61Service } from "../utils/garage61Service";

/**
 * Return type for the useRaceStrategy hook
 * 
 * Responsibility: Defines the complete interface for race strategy management
 * Used by: HomePage and other components that need strategy data
 */
interface RaceStrategyHookReturn {
  // ============================================================================
  // STATE VALUES
  // ============================================================================
  
  /** Currently selected track ID - REMOVED */
  // selectedTrackId: string;
  
  /** Currently selected car class ID - REMOVED */
  // selectedCarClassId: string;
  
  /** Race duration in minutes */
  raceDuration: number;
  
  /** Driver performance data */
  driverData: DriverData;
  
  /** Garage61 integration data - COMMENTED OUT */
  // garage61Data: any;
  
  /** Whether Garage61 data is loading - COMMENTED OUT */
  // isLoadingGarage61: boolean;
  
  /** Available Garage61 sessions - COMMENTED OUT */
  // garage61Sessions: Garage61Session[];
  
  /** Whether Garage61 sessions are loading - COMMENTED OUT */
  // isLoadingSessions: boolean;
  
  /** User's actual achievable lap time for fuel save analysis */
  userActualLapTime: number | null;
  
  /** User's actual fuel consumption when fuel saving */
  userActualFuelConsumption: number | null;
  
  /** iRacing integration data */
  // iracingTracks: IRacingTrack[];
  
  /** iRacing car classes */
  // iracingCarClasses: IRacingCarClass[];
  
  /** Whether iRacing data is loading */
  // isLoadingIRacing: boolean;
  
  // ============================================================================
  // DATA OBJECTS
  // ============================================================================
  
  /** Currently selected track object - REMOVED */
  // selectedTrack: Track | undefined;
  
  /** Currently selected car class object - REMOVED */
  // selectedCarClass: CarClass | undefined;
  
  /** All available tracks - REMOVED */
  // tracks: Track[];
  
  /** All available car classes - REMOVED */
  // carClasses: CarClass[];
  
  /** All available car categories */
  categories: string[];
  
  /** Calculated pit stop details */
  pitStopDetails: PitStopDetails;
  
  // ============================================================================
  // CALCULATED STRATEGIES
  // ============================================================================
  
  /** Normal race strategy (flat out) */
  normalStrategy: Strategy | null;
  
  /** Fuel save strategy (if applicable) */
  fuelSaveStrategy: Strategy | null;
  
  // ============================================================================
  // ACTION FUNCTIONS
  // ============================================================================
  
  /** Set the selected track ID */
  // setSelectedTrackId: (id: string) => void;
  
  /** Set the selected car class ID - REMOVED */
  // setSelectedCarClassId: (id: string) => void;
  
  /** Set the race duration */
  setRaceDuration: (duration: number) => void;
  
  /** Update driver data */
  updateDriverData: (updates: Partial<DriverData>) => void;
  
  /** Set user's actual achievable lap time */
  setUserActualLapTime: (time: number | null) => void;
  
  /** Set user's actual fuel consumption when fuel saving */
  setUserActualFuelConsumption: (consumption: number | null) => void;
  
  /** Authenticate with iRacing */
  // authenticateIRacing: (credentials: IRacingCredentials) => Promise<boolean>;
  
  /** Logout from iRacing */
  // logoutFromIRacing: () => void;
  
  /** Fetch Garage61 data - COMMENTED OUT */
  // fetchGarage61Data: () => Promise<void>;
  
  /** Logout from Garage61 - COMMENTED OUT */
  // logoutFromGarage61: () => void;
  
  /** Select a Garage61 session - COMMENTED OUT */
  // selectGarage61Session: (session: Garage61Session) => void;
  
  // ============================================================================
  // SERVICE STATE
  // ============================================================================
  
  /** Whether user is authenticated with Garage61 - COMMENTED OUT */
  // isGarage61Authenticated: boolean;
  
  /** Whether user is authenticated with iRacing */
  // isIRacingAuthenticated: boolean;
}

/**
 * Custom hook for managing race strategy calculations
 * 
 * Responsibility: Orchestrates all race strategy state management and calculations
 * Used by: HomePage component
 * 
 * This hook provides:
 * - Complete state management for race configuration
 * - Strategy calculations using dependency injection
 * - Data service integration
 * - Garage61 integration
 * - Fuel save analysis
 * 
 * @returns Object containing state, data, strategies, and actions
 */
export function useRaceStrategy(): RaceStrategyHookReturn {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  /** Currently selected track ID - REMOVED */
  // const [selectedTrackId, setSelectedTrackId] = useState<string>("daytona");
  
  /** Race duration in minutes — lazy-init from localStorage */
  const [raceDuration, setRaceDuration] = useState<number>(() => {
    const v = typeof window !== 'undefined' ? localStorage.getItem('raceDuration') : null;
    return v !== null && !isNaN(Number(v)) && Number(v) > 0 ? Number(v) : 360;
  });
  
  /** Currently selected car class ID - REMOVED */
  // const [selectedCarClassId, setSelectedCarClassId] = useState<string>("gt3_ferrari_296");
  
  /** Driver performance data state — lazy-init from localStorage */
  const [driverData, setDriverData] = useState<DriverData>(() => {
    const get = (key: string, fallback: number): number => {
      if (typeof window === 'undefined') return fallback;
      const v = localStorage.getItem(key);
      return v !== null && !isNaN(Number(v)) && Number(v) > 0 ? Number(v) : fallback;
    };
    return {
      avgLapTime: get('avgLapTime', 107.5),
      fuelConsumption: get('fuelConsumption', 3.2),
      fuelCapacity: get('fuelCapacity', 120),
      pitStopDetails: {
        pitInTime: get('pitInTime', 15),
        fuelingTime: get('fuelingTime', 50),
        pitOutTime: get('pitOutTime', 10),
        refuelTime: 0,
        totalTime: 0
      },
      trafficImpact: 0,
      trafficFuelAdjustment: 0,
      accountForFasterClass: false,
      estimatedFastestClassLapTime: 0
    };
  });

  /** Garage61 integration data - COMMENTED OUT */
  // const [garage61Data, setGarage61Data] = useState<any>(null);
  
  /** Whether Garage61 data is loading - COMMENTED OUT */
  // const [isLoadingGarage61, setIsLoadingGarage61] = useState<boolean>(false);
  
  /** Whether Garage61 sessions are loading - COMMENTED OUT */
  // const [isLoadingSessions, setIsLoadingSessions] = useState<boolean>(false);
  
  /** Available Garage61 sessions - COMMENTED OUT */
  // const [garage61Sessions, setGarage61Sessions] = useState<Garage61Session[]>([]);
  
  /** User's actual achievable lap time for fuel save analysis */
  const [userActualLapTime, setUserActualLapTime] = useState<number | null>(null);
  
  /** User's actual fuel consumption when fuel saving */
  const [userActualFuelConsumption, setUserActualFuelConsumption] = useState<number | null>(null);
  
  /** iRacing tracks from API */
  // const [iracingTracks, setIracingTracks] = useState<IRacingTrack[]>([]);
  
  /** iRacing car classes from API */
  // const [iracingCarClasses, setIracingCarClasses] = useState<IRacingCarClass[]>([]);
  
  /** Whether iRacing data is loading */
  // const [isLoadingIRacing, setIsLoadingIRacing] = useState<boolean>(false);

  // ============================================================================
  // DEPENDENCY INJECTION
  // ============================================================================
  
  /** Strategy calculator service */
  const strategyCalculator: IStrategyCalculator = useMemo(() => 
    StrategyCalculatorFactory.create(), []
  );
  
  /** Fuel save calculator service - COMMENTED OUT (not used directly) */
  // const fuelSaveCalculator = useMemo(() => 
  //   new FuelSaveCalculator(), []
  // );
  
  /** Garage61 service - COMMENTED OUT */
  // const garage61Service: IGarage61Service = useMemo(() => 
  //   Garage61ServiceFactory.create(), []
  // );
  
  /** iRacing service */
  // const iracingService: IIRacingService = useMemo(() => 
  //   IRacingServiceFactory.create(), []
  // );

  // ============================================================================
  // DERIVED STATE
  // ============================================================================
  
  /** Currently selected track object - REMOVED */
  // const selectedTrack = useMemo(() => 
  //   DataService.getTrackById(selectedTrackId), [selectedTrackId]
  // );
  
  /** Currently selected car class object - REMOVED */
  // const selectedCarClass = useMemo(() => 
  //   DataService.getCarClassById(selectedCarClassId), [selectedCarClassId]
  // );

  /** Calculated pit stop details */
  const pitStopDetails = useMemo(() => {
    const details = { ...driverData.pitStopDetails };
    
    // Calculate refuel rate from fueling time and fuel capacity
    // refuelTime = fuelingTime / fuelCapacity (seconds per liter)
    details.refuelTime = details.fuelingTime / driverData.fuelCapacity;
    
    // Calculate total pit stop time
    details.totalTime = details.pitInTime + details.fuelingTime + details.pitOutTime;
    
    console.log(`🔧 PIT STOP DETAILS CALCULATION:`, {
      'Fuel Capacity': driverData.fuelCapacity,
      'Fueling Time': details.fuelingTime,
      'Calculated Refuel Rate': details.refuelTime,
      'Pit In Time': details.pitInTime,
      'Pit Out Time': details.pitOutTime,
      'Total Time': details.totalTime,
      'Expected Refuel Rate': `${details.fuelingTime}s ÷ ${driverData.fuelCapacity}L = ${details.refuelTime.toFixed(3)}s/L`
    });
    
    return details;
  }, [driverData.pitStopDetails, driverData.fuelCapacity]);

  /** Race event configuration - simplified */
  const raceEvent = useMemo((): RaceEvent | null => {
    // Create a minimal race event with just the duration
    // Track and car class selection removed for simplified configuration
    return {
      track: { 
        id: "custom", 
        name: "Custom Track", 
        location: "Custom",
        length: 0, // Not used in calculations
        turns: 0   // Not used in calculations
      },
      duration: raceDuration,
      carClasses: [{ 
        id: "custom", 
        name: "Custom Car", 
        category: "Custom", 
        avgFuelConsumption: driverData.fuelConsumption,
        fuelCapacity: driverData.fuelCapacity, // Use driver's fuel capacity
        avgLapTime: driverData.avgLapTime      // Use driver's lap time
      }]
    };
  }, [raceDuration, driverData.fuelConsumption, driverData.fuelCapacity, driverData.avgLapTime]);

  // ============================================================================
  // STRATEGY CALCULATIONS
  // ============================================================================
  
  /** Normal race strategy (flat out) */
  const normalStrategy = useMemo((): Strategy | null => {
    if (!raceEvent) return null;
    
    console.log("Calculating normal strategy with:", {
      raceEvent,
      driverData,
      pitStopDetails
    });
    
    // Create driver data with calculated pit stop details
    const driverDataWithCalculatedPitStop = {
      ...driverData,
      pitStopDetails: pitStopDetails
    };
    
    const strategy = strategyCalculator.calculateStrategy(
      raceEvent,
      driverDataWithCalculatedPitStop,
      "custom", // Use custom car class ID
      userActualLapTime,
      userActualFuelConsumption
    );
    
    console.log("Normal strategy calculated:", strategy);
    return strategy;
  }, [raceEvent, driverData, pitStopDetails, userActualLapTime, userActualFuelConsumption, strategyCalculator]);

  /** Fuel save strategy (if applicable) */
  const fuelSaveStrategy = useMemo((): Strategy | null => {
    console.log("Fuel save strategy calculation - normalStrategy:", normalStrategy);
    
    if (!normalStrategy) {
      console.log("No fuel save strategy - missing normalStrategy");
      return null;
    }
    
    if (!normalStrategy.fuelSave) {
      console.log("No fuel save strategy - missing fuelSave in normalStrategy");
      console.log("normalStrategy keys:", Object.keys(normalStrategy));
      return null;
    }
    
    console.log("Fuel Save Analysis:", normalStrategy.fuelSave);
    
    // Check if fuel saving is worthwhile
    const fuelSaveCheck = {
      hasNormalStrategy: !!normalStrategy,
      hasRaceEvent: !!raceEvent,
      fuelSave: normalStrategy.fuelSave,
      isWorthwhile: normalStrategy.fuelSave.isWorthwhile
    };
    
    console.log("Fuel Save Strategy Check:", fuelSaveCheck);
    
    // Always create fuel save strategy if fuel save analysis exists
    // The isWorthwhile check is handled in the UI display
    console.log("Creating fuel save strategy regardless of isWorthwhile");
    
    console.log("Creating fuel save strategy:", normalStrategy.fuelSave.adjustedStrategy);
    return normalStrategy.fuelSave.adjustedStrategy;
  }, [normalStrategy, raceEvent, userActualLapTime]);

  // ============================================================================
  // DATA ACCESS
  // ============================================================================
  
  /** All available tracks - REMOVED */
  // const tracks = useMemo(() => DataService.getAllTracks(), []);
  
  /** All available car classes - REMOVED */
  // const carClasses = useMemo(() => DataService.getAllCarClasses(), []);
  
  /** All available car categories */
  const categories = useMemo(() => DataService.getCategories(), []);

  // ============================================================================
  // ACTION FUNCTIONS
  // ============================================================================
  
  /** Update driver data */
  const updateDriverData = useCallback((updates: Partial<DriverData>) => {
    console.log("updateDriverData called with:", updates);
    setDriverData(prev => {
      const newData = { ...prev, ...updates };
      console.log("New driver data:", newData);
      return newData;
    });
  }, []);

  /** Fetch Garage61 data - COMMENTED OUT */
  /*
  const fetchGarage61Data = useCallback(async () => {
    if (!selectedTrack || !selectedCarClass) return;
    
    setIsLoadingGarage61(true);
    try {
      const data = await garage61Service.fetchDriverData(
        selectedTrack.id,
        selectedCarClass.id
      );
      
      if (data) {
        setGarage61Data(data);
        // Auto-update driver data with Garage61 values
        setDriverData(prev => ({
          ...prev,
          avgLapTime: data.avgLapTime,
          fuelConsumption: data.fuelConsumption,
          fuelCapacity: data.fuelCapacity
        }));
      }
    } catch (error) {
      console.error("Error fetching Garage61 data:", error);
    } finally {
      setIsLoadingGarage61(false);
    }
  }, [selectedTrack, selectedCarClass, garage61Service]);
  */

  /** Logout from Garage61 - COMMENTED OUT */
  /*
  const logoutFromGarage61 = useCallback(() => {
    setGarage61Data(null);
  }, [garage61Service]);
  */

  /** Select a Garage61 session - COMMENTED OUT */
  /*
  const selectGarage61Session = useCallback((session: Garage61Session) => {
    setGarage61Data(session);
    
    // Auto-update driver data with selected session values
    setDriverData(prev => ({
      ...prev,
      avgLapTime: session.averageLapTime,
      fuelConsumption: session.averageFuelConsumption,
      fuelCapacity: session.fuelCapacity
    }));
  }, []);
  */

  /** Whether user is authenticated with Garage61 - COMMENTED OUT */
  /*
  const isGarage61Authenticated = useMemo(() => {
    return !!garage61Data;
  }, [garage61Data]);
  */

  /** Authenticate with iRacing */
  // const authenticateIRacing = useCallback(async (credentials: IRacingCredentials): Promise<boolean> => {
  //   setIsLoadingIRacing(true);
  //   try {
  //     const response = await iracingService.authenticate(credentials);
  //     
  //     if (response.success) {
  //       console.log('Authentication successful, sessionId:', response.sessionId);
  //       // If we have a sessionId, fetch data immediately
  //       if (response.sessionId) {
  //         console.log('Fetching tracks and car classes...');
  //         const [tracks, carClasses] = await Promise.all([
  //           iracingService.getTracks(),
  //           iracingService.getCarClasses()
  //         ]);
  //         
  //         setIracingTracks(tracks);
  //         setIracingCarClasses(carClasses);
  //       } else {
  //         console.log('No sessionId, skipping data fetch (OAuth redirect)');
  //       }
  //       // If no sessionId (OAuth redirect), don't fetch data yet
  //       // Data will be fetched when user returns from OAuth
  //       
  //       return true;
  //     }
  //     
  //     return false;
  //   } catch (error) {
  //     console.error("Error authenticating with iRacing:", error);
  //     return false;
  //   } finally {
  //     setIsLoadingIRacing(false);
  //   }
  // }, [iracingService]);

  /** Logout from iRacing */
  // const logoutFromIRacing = useCallback(() => {
  //   iracingService.logout();
  //   setIracingTracks([]);
  //   setIracingCarClasses([]);
  // }, [iracingService]);

  /** Whether user is authenticated with iRacing */
  // const isIRacingAuthenticated = useMemo(() => {
  //   return iracingService.isAuthenticated();
  // }, [iracingService]);

  // ============================================================================
  // RETURN OBJECT
  // ============================================================================
  
  return {
    // State values - simplified configuration
    // selectedTrackId,
    // selectedCarClassId,
    raceDuration,
    driverData,
    // garage61Data,
    // isLoadingGarage61,
    // garage61Sessions,
    // isLoadingSessions,
    userActualLapTime,
    userActualFuelConsumption,
    // iracingTracks,
    // iracingCarClasses,
    // isLoadingIRacing,
    
    // Data objects - simplified configuration
    // selectedTrack,
    // selectedCarClass,
    // tracks,
    // carClasses,
    categories,
    pitStopDetails,
    
    // Calculated strategies
    normalStrategy,
    fuelSaveStrategy,
    
    // Action functions - simplified configuration
    // setSelectedTrackId,
    // setSelectedCarClassId,
    setRaceDuration,
    updateDriverData,
    setUserActualLapTime,
    setUserActualFuelConsumption,
    // authenticateIRacing,
    // logoutFromIRacing,
    // fetchGarage61Data,
    // logoutFromGarage61,
    // selectGarage61Session,
    
    // Service state
    // isGarage61Authenticated
    // isIRacingAuthenticated
  };
}
